import React, { useState, useEffect, useContext } from 'react';
import {
  Flex,
  Button,
  Box,
  theme,
  Flow,
  Input,
  Label,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import {
  UpdateOrganizationByIdQuery,
  UpdateOrganizationByIdMutationVariables,
  UpdateOrganizationByIdMutationResult,
  GetOrganizationByIdQuery,
} from '../../../../../schema/generated/schema';
import { AuthContext } from '@xometry/auth-context';
import TooltipBlueIcon from '../../../../../components/TooltipBlueIcon';
import UserInitial from '../../../../../components/UserInitialBox';
import { SideBarHeader } from '../../SideBar';
import NotificationContext from '../../../../../contexts/Notification';

interface Props {
  setTeamDetailsEditPanelOpen: (isOpen: boolean) => void;
  orgName?: string;
  orgId?: string;
  isStaff: boolean;
}

const TeamDetailsEditPanel = (props: Props) => {
  const {
    setTeamDetailsEditPanelOpen,
    orgName = '',
    orgId = '',
    isStaff,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [newOrgName, setNewOrgName] = useState(orgName);
  const [updateOrganizationById] = useMutation<
    UpdateOrganizationByIdMutationResult,
    UpdateOrganizationByIdMutationVariables
  >(UpdateOrganizationByIdQuery, {
    refetchQueries: [GetOrganizationByIdQuery, 'GetOrganizationById'],
  });

  const { user } = useContext(AuthContext);
  const { addNotification } = useContext(NotificationContext);

  useEffect(() => {
    if (isSuccessful) {
      addNotification('success', 'Team Information successfully updated!');
      setTeamDetailsEditPanelOpen(false);
    }
  }, [isSuccessful]);

  const submitUpdate = async () => {
    setIsSubmitting(true);
    const { data: updateResult } = await updateOrganizationById({
      variables: {
        id: orgId,
        organization: {
          name: newOrgName,
        },
      },
    });
    if (updateResult?.updateOrganizationById.name === newOrgName) {
      setIsSuccessful(true);
      setTeamDetailsEditPanelOpen(false);
      if (window.analytics && user) {
        window.analytics.track('Team Name Edited', {
          isStaff: isStaff,
          userId: user.id,
          OrganizationId: orgId,
          email: user.emailAddress,
        });
      }
    }
    setIsSubmitting(false);
  };
  return (
    <Box color={theme.colors.darkBlues[90]}>
      <Box
        id="TeamDetailsEditPanel"
        marginTop={theme.space[4]}
        css={css`
          gap: 12px;
        `}
      >
        <Label>
          Team Name
          <TooltipBlueIcon placement="top">
            Team Names can only be between 3 and 128 characters in length and
            must contain at least 3 letters (a-z or A-Z).
          </TooltipBlueIcon>
        </Label>
        <Flex gap={theme.space[2]}>
          <Flex flex="1">
            <Input
              id="teamName"
              scale="large"
              inline={true}
              width={'100%'}
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
            />
          </Flex>
          <Button
            fontWeight={theme.fontWeights.regular}
            disabled={isSubmitting}
            onClick={() => setTeamDetailsEditPanelOpen(false)}
            scale="large"
          >
            Cancel
          </Button>
          <Button
            kind={theme.kind.primary}
            fontWeight={theme.fontWeights.regular}
            disabled={isSubmitting}
            onClick={submitUpdate}
            scale="large"
          >
            {isSubmitting ? <Flow kind={'primary'} /> : 'Submit'}
          </Button>
        </Flex>
      </Box>
      <Box marginTop={theme.space[4]}>
        Team Name Preview
        <Flex>
          <SideBarHeader itemKey="header">
            <UserInitial fullName={newOrgName} role="org" size={30} />
            <Flex
              alignContent="center"
              flexWrap="wrap"
              color={theme.colors.darkBlues[90]}
              fontWeight={600}
            >
              {newOrgName}
            </Flex>
          </SideBarHeader>
        </Flex>
      </Box>
    </Box>
  );
};

export default TeamDetailsEditPanel;
